import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import { NounKeys } from '../../constants/nouns';
import { all3pProductKeys, products1P } from '../../constants/products';
import {
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../constants/schemas/3p-config';
import { isProductConnected } from '../connectors';

/**
 * Shared contract for the `viewed searchPageFilters` analytics event.
 *
 * Both the non-lite right rail (`FireSearchPageFiltersViewedFromRail`) and the
 * Lite app filter bar (`getPixelVisibleApps`) build the `visibleApps` payload the
 * same way: app **identity** comes from the rendered DOM (`data-testid`, which is
 * inherently a render/visibility property and the contract pollinator depends on),
 * while **auth** is derived from state via {@link getVisibleAppAuthStatus} — the
 * same `isProductConnected` derivation the filter UI uses to render its "Connect"
 * affordance. Keeping both in one place stops the two readers from drifting apart.
 */

export const FILTER_ALL_TESTID = 'filter-all';
export const FILTER_GROUP_TESTID_PREFIX = 'filter-group-';
export const FILTER_TESTID_PREFIX = 'filter-';

/**
 * Strictly enforce which app filter keys we will emit
 * into the analytics payload. Anything else parsed from a `data-testid`
 * is rejected, so an unrelated descendant testid like `filter-confluence-active`
 * (a checkbox / icon inside a filter row) cannot leak a spurious
 * `confluence-active` appKey into the analytics payload.
 *
 * 3P keys are included from the static `all3pProductKeys` list (not only
 * the runtime `thirdPartyConfigs` map) because some 3P apps — e.g. Loom —
 * don't require OAuth and therefore aren't added to `thirdPartyConfigs`,
 * which would otherwise silently drop them from the visible-apps payload.
 */
export const STATIC_KNOWN_APP_KEYS: ReadonlySet<string> = new Set<string>([
	'all',
	...Object.keys(products1P),
	...all3pProductKeys,
	...Object.values(NounKeys),
]);

/**
 * Build the set of known app keys we are willing to emit.
 */
export const buildKnownAppKeySet = (
	thirdPartyConfigs: Partial<Record<ProductKeys3P, ThirdPartyConfigsWithAPIResponse>> | undefined,
): Set<string> => {
	if (!thirdPartyConfigs) {
		return new Set<string>(STATIC_KNOWN_APP_KEYS);
	}
	const known = new Set<string>(STATIC_KNOWN_APP_KEYS);
	Object.keys(thirdPartyConfigs).forEach((key) => known.add(key));
	return known;
};

export const parseAppKeyFromTestId = (
	testId: string,
	knownAppKeys: ReadonlySet<string>,
): string | undefined => {
	if (testId === FILTER_ALL_TESTID) {
		return 'all';
	}
	let candidate: string | undefined;
	if (testId.startsWith(FILTER_GROUP_TESTID_PREFIX)) {
		candidate = testId.slice(FILTER_GROUP_TESTID_PREFIX.length) || undefined;
	} else if (testId.startsWith(FILTER_TESTID_PREFIX)) {
		candidate = testId.slice(FILTER_TESTID_PREFIX.length) || undefined;
	}
	if (!candidate) {
		return undefined;
	}
	return knownAppKeys.has(candidate) ? candidate : undefined;
};

/**
 * Auth status for a visible app in the `visibleApps` payload, derived from state
 * (not the DOM) so both modes use the identical derivation the filter UI renders
 * from. The "all" filter has no auth concept → `null`; everything else is
 * `isProductConnected` (first-party / connected apps → `true`, apps still needing
 * OAuth → `false`).
 */
export const getVisibleAppAuthStatus = (
	appKey: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined,
): boolean | null =>
	appKey === 'all' ? null : isProductConnected(appKey, thirdPartyConfigs ?? {});
